import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const AppointmentOverview = ({
  appointments = [],
  onViewAll,
  onCreateNew,
  onAppointmentClick,
  staffFilterId,
  onStaffFilterChange,
  staffOptions = [],
}) => {
  const { t } = useTranslation();
  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-success text-success-foreground";
      case "pending":
        return "bg-warning text-warning-foreground";
      case "cancelled":
        return "bg-error text-error-foreground";
      case "completed":
        return "bg-primary text-primary-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "confirmed":
        return t("calendar.statusConfirmed");
      case "pending":
        return t("calendar.statusPending");
      case "cancelled":
        return t("calendar.statusCancelled");
      case "completed":
        return t("calendar.statusCompleted");
      case "noshow":
        return t("calendar.statusNoShow");
      default:
        return status;
    }
  };

  return (
    <div className="bg-background border border-border rounded-lg p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-foreground shrink-0">{t("dashboard.appointmentsToday")}</h2>
          {staffOptions.length > 1 && (
            <select
              value={staffFilterId || "all"}
              onChange={(e) => onStaffFilterChange?.(e.target.value)}
              className="border border-border bg-background rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary max-w-[180px]"
            >
              {staffOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onViewAll}
            className="!px-0 md:!px-3"
          >
            <Icon name="Calendar" size={16} className="mr-2" />
            {t("common.viewAll")}
          </Button>

          <Button variant="default" size="sm" onClick={onCreateNew}>
            <Icon name="Plus" size={16} className="mr-2" />
            {t("calendar.newAppointment")}
          </Button>
        </div>
      </div>

      {appointments?.length === 0 ? (
        <div className="text-center py-8">
          <Icon
            name="Calendar"
            size={48}
            className="mx-auto text-muted-foreground mb-4"
          />
          <p className="text-text-secondary">{t("calendar.noAppointments")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {appointments?.map((appointment) => (
            <button
              key={appointment?.id}
              type="button"
              onClick={() => onAppointmentClick?.(appointment)}
              className="text-left p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-medium text-foreground truncate flex-1 min-w-0">
                  {appointment?.clientName}
                </h3>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                    appointment?.status
                  )}`}
                >
                  {getStatusText(appointment?.status)}
                </span>
              </div>

              <p className="text-sm text-text-secondary truncate">{appointment?.serviceName}</p>

              {/* 3rd row: time • personal • duration */}
              <div className="mt-2 text-xs text-text-secondary flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex items-center">
                  <Icon name="Clock" size={12} className="mr-1" />
                  {appointment?.time}
                </span>
                <span className="flex items-center">
                  <Icon name="Users" size={12} className="mr-1" />
                  {appointment?.staffName}
                </span>
                <span className="flex items-center">
                  <Icon name="Clock" size={12} className="mr-1" />
                  {appointment?.duration} min
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentOverview;
